import { ApiErrorCode, getApiErrorCode, useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { EdictTransferResponse, TransferEdictRequest, edictsApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

/**
 * Runs the "§" transfer for one edict. The backend writes everything or nothing, so a failure needs no
 * cleanup here — it only has to say why, which is what the domain error code on the response carries.
 */
export function useTransferEdict(onTransferred: (result: EdictTransferResponse) => void) {
  const { t } = useTranslation();
  const { showError } = useAlertContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transferEdictRequest: TransferEdictRequest) =>
      edictsApi.transferEdict({ transferEdictRequest }, withLocalErrorHandling),
    onSuccess: async (result, { clientId }) => {
      // Everything the transfer writes: the Verfahren (list and detail share the key prefix), the Aufgabe,
      // the hearing appointment, the Tagsatzung case note — and the Aktenzahl and Gericht, which are
      // Detailsuche filters on a result list cached with staleTime: Infinity.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKey.SrvProceeding, clientId] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, clientId] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.Clients] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.Assignments] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.Appointments] }),
      ]);
      onTransferred(result);
    },
    onError: async (error: unknown) => {
      const code = await getApiErrorCode(error);

      if (code === ApiErrorCode.EdictNotDebtSettlementRegister) {
        showError(t("edicts.transferErrors.notDebtSettlementRegister"));
        return;
      }

      if (code === ApiErrorCode.EdictSourceUnavailable || code === ApiErrorCode.EdictDocumentNotFound) {
        showError(t("edicts.transferErrors.sourceUnavailable"));
        return;
      }

      showError(t("edicts.transferErrors.unknown"));
    },
  });
}
